// ─── AssessForm.js ────────────────────────────────────────────────────────────
// Project Manager assessment interface.
// Shows areas one at a time: photos (hero), BDM measurements (read-only),
// and a simple hours input per substrate group.

import React, { useState, useEffect, useRef } from 'react';
import { entryM2, calcAreaHours, DEFAULT_GIRTHS } from './calcUtils';
import { CONDITION_LEVELS, SITE_ITEMS, SUBSTRATE_FINISHES, TREATMENT_LEVELS, RAG_DEFAULT_TREATMENT, TREATMENT_BY_KEY, REFERRAL_SUGGEST_DEFECTS } from './areaConstants';
import { familyForSubstrate, defectsForFamily, generateConditionNote, resolveSubstrateOutcome } from './conditionLibrary';
import { conditionToRag } from './reportViewModel';
import { uploadPhoto } from './areaFormUtils';
import { countRecordedSiteItems } from './dataModel';
import AccessTags from './AccessTags';
import ConfirmModal from './ConfirmModal';
import SiteItemsForm from './SiteItemsForm';
import FlooringForm from './FlooringForm';
import Icon from './Icons';
import { showToast } from './Toast';

const safe = (arr) => Array.isArray(arr) ? arr : [];

// ── Add Missing Area modal — used by assessor in picker ─────────────────────
function AddMissingAreaModal({ onAdd, onClose }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('external');
  const [surfaces, setSurfaces] = useState([]);
  const [note, setNote] = useState('');
  const surfaceOptions = type === 'internal'
    ? ['Ceilings', 'Walls', 'Floors', 'Windows', 'Doors', 'Skirting', 'Stairs']
    : ['Gutter', 'Fascia', 'Eaves', 'Soffit', 'Walls', 'Windows', 'Floors', 'Downpipes', 'Slab Edge', 'Doors', 'Balustrade / Handrails', 'Posts', 'Roofs'];
  const toggleSurface = (s) => setSurfaces(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  return (
    <div className="cm-overlay" onClick={onClose}>
      <div className="cm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="cm-title">＋ Add Missing Area</div>
        <div className="cm-message" style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
          Flag an area the measurer missed. They'll be notified to add measurements.
        </div>

        <div className="as-add-field">
          <label>Area name</label>
          <input type="text" placeholder="e.g. Level 2 Corridor" value={name} autoFocus
            onChange={e => setName(e.target.value)} className="as-add-input" />
        </div>

        <div className="as-add-field">
          <label>Type</label>
          <div className="as-add-type-row">
            <button className={`as-add-type-btn ${type === 'internal' ? 'active' : ''}`}
              onClick={() => { setType('internal'); setSurfaces([]); }}>🏠 Internal</button>
            <button className={`as-add-type-btn ${type === 'external' ? 'active' : ''}`}
              onClick={() => { setType('external'); setSurfaces([]); }}>🏗️ External</button>
          </div>
        </div>

        <div className="as-add-field">
          <label>Surfaces to measure</label>
          <div className="as-add-surfaces">
            {surfaceOptions.map(s => (
              <button key={s} className={`af-tag ${surfaces.includes(s) ? 'af-tag-active' : ''}`}
                onClick={() => toggleSurface(s)}>{s}</button>
            ))}
          </div>
        </div>

        <div className="as-add-field">
          <label>PM note <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
          <textarea className="as-pm-notes" rows={3} placeholder="e.g. Check behind reception desk, include both sides..."
            value={note} onChange={e => setNote(e.target.value)} />
        </div>

        <div className="cm-btns">
          <button className="cm-btn cm-cancel" onClick={onClose}>Cancel</button>
          <button className="cm-btn cm-confirm" disabled={!name.trim()}
            onClick={() => onAdd({ name: name.trim(), type, pmSurfaces: surfaces, pmNote: note.trim() })}>
            Add Area
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Per-substrate summary from measurement data ──────────────────────────────
function getSubstrateSummary(area) {
  const entries = safe(area.surfaceEntries);
  const groups = area.repeatGroups || [];
  const gc = {};
  groups.forEach(g => { gc[g.id] = g.count || 1; });

  const bySubstrate = {};
  entries.forEach(e => {
    const k = e.substrate; if (!k) return;
    if (!bySubstrate[k]) bySubstrate[k] = { entries: [], m2: 0, hours: 0 };
    bySubstrate[k].entries.push(e);
  });

  const settings = area.surfaceSettings || {};
  Object.entries(bySubstrate).forEach(([sub, data]) => {
    const s = settings[sub] || {};
    const base = s.baseSurface || sub;
    const girth = s.girth ?? DEFAULT_GIRTHS[base] ?? 1;
    const rate = s.paintingSpeed ?? 16;
    const coats = s.coats ?? (sub === 'Doors' ? 3 : 2);
    const prepRate = s.prepRate ?? 75;

    data.entries.forEach(e => {
      const rc = e.repeatGroupId ? (gc[e.repeatGroupId] || 1) : 1;
      const m2 = entryM2(sub, e) * rc;
      const qty = m2 * girth;
      const hours = qty * coats / rate + (prepRate > 0 ? qty / prepRate : 0);
      data.m2 += m2;
      data.hours += hours;
    });
  });

  return bySubstrate;
}

// ── Photo carousel ───────────────────────────────────────────────────────────
function PhotoCarousel({ photos }) {
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  if (!photos || photos.length === 0) return <div className="as-no-photos">No site photos for this area</div>;

  const photo = photos[Math.min(idx, photos.length - 1)];
  return (
    <div className="as-photos">
      <div className="as-photo-main" onClick={() => setLightbox(true)}>
        <img src={photo.url} alt="site" />
        {photo.isDefect && <div className="as-defect-overlay">⚠ Defect</div>}
        {photo.direction && <div className="as-dir-overlay">🧭 {photo.direction}</div>}
        <div className="as-photo-count">{idx + 1} / {photos.length}</div>
      </div>
      {photos.length > 1 && (
        <div className="as-photo-nav">
          <button className="as-nav-btn" disabled={idx === 0} onClick={() => setIdx(i => i - 1)}>‹ Prev</button>
          <div className="as-photo-dots">
            {photos.map((_, i) => (
              <span key={i} className={`as-dot ${i === idx ? 'active' : ''}`} onClick={() => setIdx(i)} />
            ))}
          </div>
          <button className="as-nav-btn" disabled={idx >= photos.length - 1} onClick={() => setIdx(i => i + 1)}>Next ›</button>
        </div>
      )}
      {lightbox && (
        <div className="s2s-lightbox" onClick={() => setLightbox(false)}>
          <button className="s2s-lb-close" aria-label="Close"><Icon name="close" size={18} /></button>
          <img src={photo.url} alt="full" />
        </div>
      )}
    </div>
  );
}

// ── Surface assess row ───────────────────────────────────────────────────────
function RapidHoursEntry({ substrate, total, onCommitTotal }) {  // onCommitTotal(value, skipAdvance)
  const [entries, setEntries] = useState([]);
  const [val, setVal] = useState('');
  const [label, setLabel] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const commit = () => {
    const v = parseFloat(val);
    if (!v || isNaN(v)) return;
    const autoLabel = label.trim() || `Run ${entries.length + 1}`;
    const next = [...entries, { label: autoLabel, hours: v }];
    setEntries(next);
    setVal(''); setLabel('');
    const sum = next.reduce((s, e) => s + e.hours, 0);
    onCommitTotal(parseFloat(sum.toFixed(2)).toString(), true);
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const remove = (i) => {
    const next = entries.filter((_, idx) => idx !== i);
    setEntries(next);
    const sum = next.reduce((s, e) => s + e.hours, 0);
    onCommitTotal(next.length ? parseFloat(sum.toFixed(2)).toString() : '', true);
  };

  const runningTotal = entries.reduce((s, e) => s + e.hours, 0);

  return (
    <div className="as-rapid-panel">
      {entries.length > 0 && (
        <div className="as-rapid-entries">
          {entries.map((e, i) => (
            <div key={i} className="as-rapid-entry">
              <span className="as-rapid-entry-label">{e.label}</span>
              <span className="as-rapid-entry-hours">{e.hours}h</span>
              <button className="as-rapid-remove" aria-label="Remove entry" onClick={() => remove(i)}><Icon name="close" size={12} /></button>
            </div>
          ))}
          <div className="as-rapid-total">
            <span>Total</span>
            <span>{runningTotal.toFixed(2)}h</span>
          </div>
        </div>
      )}
      <div className="as-rapid-inputs">
        <input ref={inputRef} className="as-rapid-hours-input" type="text" inputMode="decimal"
          placeholder="Hours" value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); }} />
        <input className="as-rapid-label-input" type="text"
          placeholder="Label (optional)" value={label}
          onChange={e => setLabel(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); }} />
        <button className="as-rapid-add-btn" onClick={commit} disabled={!val || isNaN(parseFloat(val))}>＋</button>
      </div>
      {entries.length === 0 && (
        <div className="as-rapid-hint">Enter hours for each section, then tap ＋ — they'll add up automatically</div>
      )}
    </div>
  );
}

function SurfaceAssessRow({ substrate, summary, assessData, conditionData, onChangeHours, onChangeNote, onChangeCondition, addedByPM }) {
  const hours = assessData?.hours ?? '';
  const note = assessData?.note ?? '';
  const condKey = conditionData?.condition || null;
  const condDefects = Array.isArray(conditionData?.defects) ? conditionData.defects : [];
  const condCustom = Array.isArray(conditionData?.customDefects) ? conditionData.customDefects : [];
  const photoException = conditionData?.photoExceptionReason || null;
  const condFamily = familyForSubstrate(substrate, null, summary?.areaType);
  const availableDefects = defectsForFamily(condFamily);
  const finish = conditionData?.finish || null;
  const schemeCompliant = conditionData?.schemeCompliant ?? null; // true | false | null
  const { outcome, reason } = resolveSubstrateOutcome({ finish, condition: condKey, defects: condDefects, customDefects: condCustom, schemeCompliant });
  const isExcluded = outcome === 'excluded';
  const treatmentKey = conditionData?.treatment || null;
  const suggestedTreatment = RAG_DEFAULT_TREATMENT[conditionToRag(condKey)] || null;
  const effectiveTreatment = treatmentKey || suggestedTreatment;
  const treatmentIsFullSpec = effectiveTreatment ? !!(TREATMENT_BY_KEY[effectiveTreatment] && TREATMENT_BY_KEY[effectiveTreatment].full) : true;
  const referral = !!conditionData?.referral;
  const referralNote = conditionData?.referralNote || '';
  const suggestReferral = !referral && condDefects.some(id => REFERRAL_SUGGEST_DEFECTS.has(id));
  const [showNote, setShowNote] = useState(!!note);
  const [rapidMode, setRapidMode] = useState(false);
  const [defectsExpanded, setDefectsExpanded] = useState(false);

  return (
    <div className={`as-surface-row ${addedByPM ? 'as-pm-added' : ''}`}>
      <div className="as-surface-header">
        <div className="as-surface-name">
          {substrate}
          {addedByPM && <span className="as-pm-badge">⚠ PM added</span>}
        </div>
        <div className="as-surface-measured">
          {summary ? (
            <>
              <span className="as-meas-m2">{summary.m2.toFixed(1)} m²</span>
              <span className="as-meas-hours">{summary.hours.toFixed(1)}h calc</span>
            </>
          ) : (
            <span className="as-meas-missing">No measurements</span>
          )}
        </div>
      </div>

      {rapidMode ? (
        <RapidHoursEntry substrate={substrate} total={hours} onCommitTotal={(v, skip) => onChangeHours(v, skip)} />
      ) : (
        <div className="as-hours-row">
          <label className="as-hours-label">Assessed hours</label>
          <input className="as-hours-input" type="text" inputMode="decimal"
            placeholder="—" value={hours}
            onChange={e => onChangeHours(e.target.value)} />
        </div>
      )}

      {onChangeCondition && (
        <div className="as-condition-row">
          <span className="as-condition-label">Condition</span>
          <div className="as-condition-pills">
            {CONDITION_LEVELS.map(c => (
              <button
                key={c.key}
                type="button"
                className={`as-cond-pill as-cond-${c.key} ${condKey === c.key ? 'active' : ''}`}
                onClick={() => onChangeCondition('condition', condKey === c.key ? null : c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="as-attr-block">
            <div className="as-attr-label">Finish</div>
            <div className="as-attr-chips">
              {SUBSTRATE_FINISHES.map(f => (
                <button
                  key={f.key}
                  type="button"
                  className={`as-attr-chip ${finish === f.key ? 'active' : ''}`}
                  onClick={() => onChangeCondition('finish', finish === f.key ? null : f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="as-attr-block">
            <div className="as-attr-label">Current colour scheme</div>
            <div className="as-attr-chips">
              <button
                type="button"
                className={`as-attr-chip ${schemeCompliant === true ? 'active' : ''}`}
                onClick={() => onChangeCondition('schemeCompliant', schemeCompliant === true ? null : true)}
              >
                In scheme
              </button>
              <button
                type="button"
                className={`as-attr-chip as-attr-chip-warn ${schemeCompliant === false ? 'active' : ''}`}
                onClick={() => onChangeCondition('schemeCompliant', schemeCompliant === false ? null : false)}
              >
                Not in scheme
              </button>
              {schemeCompliant === null && <span className="as-attr-neutral">Not assessed</span>}
            </div>
          </div>

          {condKey && isExcluded && (
            <div className="as-excluded-row">
              <span className="as-excluded-pill">✓ {reason || 'No work required'}</span>
              <button type="button" className="as-excluded-toggle" onClick={() => setDefectsExpanded(v => !v)}>
                {defectsExpanded ? 'Hide details' : 'Show details'}
              </button>
            </div>
          )}

          {condKey && (!isExcluded || defectsExpanded) && (
            <div className="as-defects-block">
              <div className="as-defects-label">Defects observed</div>
              <div className="as-defect-chips">
                {availableDefects.map(d => {
                  const on = condDefects.includes(d.id);
                  return (
                    <button
                      key={d.id}
                      type="button"
                      className={`as-defect-chip ${on ? 'active' : ''} ${d.makeGood ? 'mk' : ''}`}
                      onClick={() => {
                        const next = on
                          ? condDefects.filter(x => x !== d.id)
                          : [...condDefects, d.id];
                        onChangeCondition('defects', next);
                      }}
                    >
                      {d.label}
                    </button>
                  );
                })}
                {condCustom.map((txt, i) => (
                  <span key={'cust'+i} className="as-defect-chip active sf-defect-custom">
                    {txt}
                    <button type="button" className="sf-chip-x" aria-label="Remove custom defect"
                      onClick={() => onChangeCondition('customDefects', condCustom.filter((_, j) => j !== i))}>×</button>
                  </span>
                ))}
                <button type="button" className="as-defect-chip as-defect-add"
                  onClick={() => {
                    const txt = (window.prompt('Describe the condition issue:') || '').trim();
                    if (txt) onChangeCondition('customDefects', [...condCustom, txt]);
                  }}>+ Add your own</button>
              </div>

              <div className="as-referral-block">
                <label className="as-referral-toggle">
                  <input type="checkbox" checked={referral}
                    onChange={e => onChangeCondition('referral', e.target.checked)} />
                  <span>⚑ Refer to client — building repair required before painting</span>
                </label>
                {suggestReferral && <div className="as-referral-hint">Building repair? Flag it for the client.</div>}
                {referral && (
                  <input className="as-referral-note" type="text" placeholder="What needs repairing, and where"
                    value={referralNote} onChange={e => onChangeCondition('referralNote', e.target.value)} />
                )}
              </div>

              {/* Photo-evidence reason (assess path: log reason if no photo) */}
              {!photoException && (
                <div className="sf-photo-nudge">
                  <span className="sf-photo-nudge-text">📷 Capture a photo of this surface as condition evidence, or log a reason.</span>
                  <div className="sf-photo-nudge-actions">
                    <select className="as-photo-exception"
                      value=""
                      onChange={e => { if (e.target.value) onChangeCondition('photoExceptionReason', e.target.value); }}>
                      <option value="">No photo — reason…</option>
                      <option value="no_access">No access</option>
                      <option value="not_reachable">Not reachable</option>
                      <option value="camera_unavailable">Camera unavailable</option>
                    </select>
                  </div>
                </div>
              )}
              {photoException && (
                <div className="as-photo-exception-set">
                  Photo skipped: {photoException === 'no_access' ? 'No access' : photoException === 'not_reachable' ? 'Not reachable' : 'Camera unavailable'}
                  <button type="button" className="sf-chip-x" aria-label="Clear reason"
                    onClick={() => onChangeCondition('photoExceptionReason', null)}>×</button>
                </div>
              )}
              <div className="as-defects-actions">
                <button
                  type="button"
                  className="as-gen-note-btn"
                  onClick={() => onChangeCondition(
                    'note',
                    generateConditionNote(substrate, { ...conditionData, condition: condKey, defects: condDefects, customDefects: Array.isArray(conditionData?.customDefects) ? conditionData.customDefects : [] })
                  )}
                >
                  ✨ Generate note
                </button>
                {conditionData?.noteEdited && <span className="as-note-edited">edited</span>}
              </div>
              <textarea
                className="as-condition-note"
                rows={3}
                placeholder="Condition note — tap Generate, then edit as needed…"
                value={conditionData?.note || ''}
                onChange={e => onChangeCondition('note', e.target.value, true)}
              />
            </div>
          )}

          {condKey && !isExcluded && (
            <div className="as-treatment-block">
              <div className="as-attr-label">Treatment</div>
              <div className="as-attr-chips">
                {TREATMENT_LEVELS.map(t => (
                  <button
                    key={t.key}
                    type="button"
                    className={`as-treatment-chip ${treatmentKey === t.key ? 'active' : (!treatmentKey && suggestedTreatment === t.key ? 'suggested' : '')}`}
                    onClick={() => onChangeCondition('treatment', treatmentKey === t.key ? null : t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {!treatmentIsFullSpec && (
                <input
                  className="as-treatment-note"
                  type="text"
                  placeholder="e.g. Already white — wash, de-cob and 1 coat"
                  value={conditionData?.treatmentNote || ''}
                  onChange={e => onChangeCondition('treatmentNote', e.target.value)}
                />
              )}
            </div>
          )}
        </div>
      )}
      <div className="as-row-footer">
        <button className="as-rapid-toggle" onClick={() => setRapidMode(m => !m)}>
          {rapidMode ? '⌨ Simple entry' : '＋ Rapid entry'}
        </button>
        {!showNote && <button className="as-note-toggle" onClick={() => setShowNote(true)}>+ Add note</button>}
      </div>
      {showNote && (
        <textarea className="as-note-input" rows={2} placeholder="Note for this surface..."
          value={note} onChange={e => onChangeNote(e.target.value)} />
      )}
    </div>
  );
}

// ── Add missed surface ───────────────────────────────────────────────────────
function AddMissedSurface({ surfaceList, onAdd }) {
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [custom, setCustom] = useState('');
  const isCustom = name === 'Other';

  const handleAdd = () => {
    const final = isCustom ? custom.trim() : name;
    if (!final) return;
    onAdd(final);
    setName(''); setCustom(''); setShow(false);
  };

  if (!show) return (
    <button className="as-add-missed" onClick={() => setShow(true)}>
      ⚠ Flag a missed surface
    </button>
  );

  return (
    <div className="as-missed-form">
      <div className="as-missed-title">Add a surface the BDM may have missed</div>
      <select value={name} onChange={e => { setName(e.target.value); setCustom(''); }}>
        <option value="">Select surface...</option>
        {surfaceList.map(s => <option key={s} value={s}>{s}</option>)}
        <option value="Other">Other (custom)...</option>
      </select>
      {isCustom && (
        <input className="as-missed-custom" autoFocus placeholder="Surface name..." value={custom}
          onChange={e => setCustom(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAdd()} />
      )}
      <div className="as-missed-btns">
        <button className="as-missed-cancel" onClick={() => setShow(false)}>Cancel</button>
        <button className="as-missed-confirm" onClick={handleAdd} disabled={isCustom ? !custom.trim() : !name}>Add</button>
      </div>
    </div>
  );
}

// ── Main AssessForm ──────────────────────────────────────────────────────────
export default function AssessForm({ project, targetAreaId, onSaveArea, onComplete, onBack, onCreatePMArea, onSaveProjectFields }) {
  const areas = project.areas || [];
  const initialIdx = targetAreaId ? areas.findIndex(a => a.firestoreId === targetAreaId) : -1;
  const [areaIdx, setAreaIdx] = useState(initialIdx >= 0 ? initialIdx : -1);
  const [subScreen, setSubScreen] = useState(null);
  const [localData, setLocalData] = useState({}); // areaId → { assessBySubstrate, assessNotes, accessNeeds, pmAddedSurfaces }
  const [saving, setSaving] = useState(false);
  const [hoursDone, setHoursDone] = useState({}); // areaId → bool: all surfaces have hours
  const [activeSubstrate, setActiveSubstrate] = useState(null); // guided step-through
  const dirty = useRef(false);
  const notesRef = useRef(null);
  const accessRef = useRef(null);
  const surfaceRowRefs = useRef({});
  const pmCameraRef = useRef(null);
  const pmUploadRef = useRef(null);
  const [showAddArea, setShowAddArea] = useState(false);

  // PM photo capture — adds to area's photos with addedBy tag
  const handlePMPhoto = (file) => {
    if (!file || areaIdx < 0) return;
    const area = areas[areaIdx];
    if (!area?.firestoreId) return;
    const areaId = area.firestoreId;
    uploadPhoto(
      file,
      project.firestoreId,
      (tempPhoto) => {
        const pmPhoto = { ...tempPhoto, addedBy: 'pm' };
        dirty.current = true;
        setLocalData(prev => ({
          ...prev,
          [areaId]: { ...prev[areaId], photos: [...(prev[areaId]?.photos || area.photos || []), pmPhoto] },
        }));
      },
      (tempId, { url, thumbnailUrl }) => {
        setLocalData(prev => ({
          ...prev,
          [areaId]: {
            ...prev[areaId],
            photos: (prev[areaId]?.photos || []).map(p => p.id === tempId ? { ...p, url, thumbnailUrl, pending: false } : p),
          },
        }));
      },
      (tempId) => {
        showToast('Photo upload failed — please try again', 'error');
        setLocalData(prev => ({
          ...prev,
          [areaId]: { ...prev[areaId], photos: (prev[areaId]?.photos || []).filter(p => p.id !== tempId) },
        }));
      }
    );
  };

  // Initialise local data from areas — merge to preserve unsaved edits
  useEffect(() => {
    setLocalData(prev => {
      const updated = { ...prev };
      areas.forEach(a => {
        if (!updated[a.firestoreId]) {
          updated[a.firestoreId] = {
            assessBySubstrate: a.assessBySubstrate || {},
            assessNotes: a.assessNotes || '',
            accessNeeds: Array.isArray(a.accessNeeds) ? [...a.accessNeeds] : [],
            accessNeedsOtherText: a.accessNeedsOtherText || '',
            accessOtherLabels: a.accessOtherLabels || {},
            pmAddedSurfaces: a.pmAddedSurfaces || [],
            washdownHours: a.washdownHours || '',
            conditionBySubstrate: a.conditionBySubstrate ? { ...a.conditionBySubstrate } : {},
            photos: a.photos || [],
          };
        }
      });
      return updated;
    });
  }, [areas.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Warn on page close
  useEffect(() => {
    const handler = (e) => { if (dirty.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Skip welcome if coming to a specific area OR all areas already assessed
  const alreadyFullyAssessed = (() => {
    const m = areas.filter(a => (a.surfaceEntries || []).length > 0 || (a.pmSurfaces || []).length > 0 || (a.pmAddedSurfaces || []).length > 0);
    if (m.length === 0) return false;
    return m.every(a => {
      const ad = a.assessBySubstrate || {};
      const subs = [...new Set([
        ...(a.surfaceEntries || []).map(e => e.substrate).filter(Boolean),
        ...(a.pmSurfaces || []),
        ...(a.pmAddedSurfaces || []),
      ])];
      return subs.length > 0 && subs.every(s => parseFloat(ad[s]?.hours) > 0);
    });
  })();
  const [showWelcome, setShowWelcome] = useState(!targetAreaId && !alreadyFullyAssessed);
  const [assessConfirm, setAssessConfirm] = useState(null);

  // Count how many areas are fully assessed (needed for both picker and form)
  const measurableAreas = areas.filter(a => (a.surfaceEntries || []).length > 0 || (a.pmSurfaces || []).length > 0 || (a.pmAddedSurfaces || []).length > 0);
  const assessedCount = measurableAreas.filter(a => {
    const ad = localData[a.firestoreId];
    if (!ad) return false;
    const measuredSubs = [...new Set((a.surfaceEntries || []).map(e => e.substrate).filter(Boolean))];
    const pmAdded = (ad.pmAddedSurfaces || []).filter(s => !measuredSubs.includes(s));
    const pmAreaSubs = (a.pmSurfaces || []).filter(s => !measuredSubs.includes(s) && !pmAdded.includes(s));
    const allSubs = [...measuredSubs, ...pmAdded, ...pmAreaSubs];
    if (allSubs.length === 0) return false;
    return allSubs.every(sub => parseFloat(ad.assessBySubstrate?.[sub]?.hours) > 0);
  }).length;
  const allAreasComplete = assessedCount === measurableAreas.length && measurableAreas.length > 0;

  // Save current area's assess data
  const saveCurrentArea = async (idx) => {
    const a = areas[idx >= 0 ? idx : areaIdx];
    if (!a?.firestoreId) return;
    const ad = localData[a.firestoreId];
    if (!ad) return;
    setSaving(true);
    try {
      // Timeout after 8s to prevent hanging
      const savePromise = onSaveArea(a.firestoreId, {
        assessBySubstrate: ad.assessBySubstrate,
        conditionBySubstrate: ad.conditionBySubstrate || {},
        assessNotes: ad.assessNotes,
        accessNeeds: ad.accessNeeds,
        accessNeedsOtherText: ad.accessNeedsOtherText || '',
        accessOtherLabels: ad.accessOtherLabels || {},
        pmAddedSurfaces: ad.pmAddedSurfaces,
        washdownHours: ad.washdownHours || '',
        photos: ad.photos,
      });
      await Promise.race([savePromise, new Promise(r => setTimeout(r, 8000))]);
      dirty.current = false;
    } catch (err) { console.error('Assess save failed:', err); }
    setSaving(false);
  };

  const handleComplete = async () => {
    if (areaIdx >= 0) { try { await saveCurrentArea(areaIdx); } catch {} }
    const incompleteAreas = measurableAreas.filter(a => {
      const ad = localData[a.firestoreId];
      if (!ad) return true;
      const measuredSubs = [...new Set((a.surfaceEntries || []).map(e => e.substrate).filter(Boolean))];
      const pmAdded = (ad.pmAddedSurfaces || []).filter(s => !measuredSubs.includes(s));
      const pmAreaSubs = (a.pmSurfaces || []).filter(s => !measuredSubs.includes(s) && !pmAdded.includes(s));
      const allSubs = [...measuredSubs, ...pmAdded, ...pmAreaSubs];
      if (allSubs.length === 0) return true;
      return !allSubs.every(sub => parseFloat(ad.assessBySubstrate?.[sub]?.hours) > 0);
    });
    if (incompleteAreas.length > 0) {
      const names = incompleteAreas.map(a => a.name || 'Unnamed').join(', ');
      setAssessConfirm({
        title: 'Assessment incomplete',
        message: `${incompleteAreas.length} area${incompleteAreas.length > 1 ? 's are' : ' is'} not fully assessed:\n\n${names}\n\nSave progress and exit? You can continue later.`,
        confirmLabel: 'Save & Exit',
        onConfirm: () => { setAssessConfirm(null); onComplete(false); },
      });
      return;
    }
    onComplete(incompleteAreas.length === 0);
  };

  const handleBack = async () => {
    if (dirty.current && areaIdx >= 0) { try { await saveCurrentArea(areaIdx); } catch {} }
    onBack();
  };

  // Scroll active surface row into view (must be before any early returns)
  useEffect(() => {
    if (!activeSubstrate) return;
    const timer = setTimeout(() => {
      const el = surfaceRowRefs.current[activeSubstrate];
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 80);
    return () => clearTimeout(timer);
  }, [activeSubstrate]);

  // ── Site-wide sub-screens (project-level, not area-level) ────────────────
  if (subScreen === 'siteItems') {
    return <SiteItemsForm
      project={project}
      onSave={async (fields) => {
        await onSaveProjectFields({ firestoreId: project.firestoreId, ...fields });
        setSubScreen(null);
      }}
      onBack={() => setSubScreen(null)} />;
  }
  if (subScreen === 'flooring') {
    return <FlooringForm
      project={project}
      onSave={async (fields) => {
        await onSaveProjectFields({ firestoreId: project.firestoreId, ...fields });
        setSubScreen(null);
      }}
      onBack={() => setSubScreen(null)} />;
  }

  // ── Area picker (areaIdx === -1) ────────────────────────────────────────
  const area = areaIdx >= 0 ? areas[areaIdx] : null;
  if (!area) {
    const internal = areas.filter(a => a.type === 'internal');
    const external = areas.filter(a => a.type === 'external');
    const recordedSiteItems = countRecordedSiteItems(project.siteItems);
    const siteItemsStatus = recordedSiteItems === 0 ? 'not' : recordedSiteItems === SITE_ITEMS.length ? 'done' : 'partial';
    const flooringCount = (project.flooring || []).length;
    const unmeasuredCount = areas.filter(a => (a.surfaceEntries || []).length === 0 && !a.pmAdded).length;
    const areaStatus = (a) => {
      const ad = localData[a.firestoreId];
      if (!ad) return 'not';
      const subs = [...new Set((a.surfaceEntries || []).map(e => e.substrate).filter(Boolean))];
      const pmSubs = (ad.pmAddedSurfaces || []).filter(s => !subs.includes(s));
      const allSubs = [...subs, ...pmSubs];
      if (allSubs.length === 0) return a.pmAdded ? 'pm' : 'not';
      const allDone = allSubs.every(sub => parseFloat(ad.assessBySubstrate?.[sub]?.hours) > 0);
      const anyDone = Object.values(ad.assessBySubstrate || {}).some(v => parseFloat(v.hours) > 0);
      return allDone ? 'done' : anyDone ? 'partial' : 'not';
    };
    const renderPickerCard = (a) => {
      const st = areaStatus(a);
      const realIdx = areas.indexOf(a);
      const surfCount = [...new Set((a.surfaceEntries||[]).map(e=>e.substrate))].length;
      const pmSurfCount = (a.pmSurfaces || []).length;
      const metaText = surfCount > 0 ? `${surfCount} surfaces` : pmSurfCount > 0 ? `${pmSurfCount} surfaces (PM)` : 'No surfaces';
      return (
        <div key={a.firestoreId} className={`as-pick-card as-pick-${st}`} onClick={() => { setAreaIdx(realIdx); window.scrollTo(0,0); }}>
          <span className="as-pick-icon">{a.type === 'internal' ? '🏠' : '🏗️'}</span>
          <div className="as-pick-info">
            <div className="as-pick-name">{a.name || 'Unnamed'}{a.pmAdded ? ' ⚠' : ''}</div>
            <div className="as-pick-meta">{metaText}{(a.photos||[]).length > 0 ? ` · ${a.photos.length} photos` : ''}</div>
          </div>
          <span className={`as-pick-status as-pick-st-${st}`}>{st === 'done' ? '✓ Done' : st === 'partial' ? '◐ Partial' : st === 'pm' ? '⚠ PM Added' : '○ Not started'}</span>
        </div>
      );
    };
    return (
      <div className="as-screen">
        {assessConfirm && (
          <ConfirmModal title={assessConfirm.title} message={assessConfirm.message}
            confirmLabel={assessConfirm.confirmLabel} onConfirm={assessConfirm.onConfirm} onCancel={() => setAssessConfirm(null)} />
        )}
        {showWelcome && (
          <ConfirmModal
            title="📋 Assessment Mode"
            message="Review each area's photos and scope, then enter your estimated paint hours per surface. Select an area to begin."
            confirmLabel="Got it"
            onConfirm={() => setShowWelcome(false)}
            onCancel={() => setShowWelcome(false)}
          />
        )}
        <div className="s2s-header">
          <button className="s2s-back-btn" onClick={handleBack}>← Back</button>
          <div className="s2s-header-center">
            <div className="s2s-header-title">📋 Assess Mode</div>
            <div className="s2s-header-sub">{project.projectName || project.clientName || 'Project'}</div>
          </div>
          <div style={{ width: 60 }} />
        </div>
        <div className="as-content" style={{paddingBottom:40}}>
          <div className="as-progress">
            <div className="as-progress-text">{assessedCount} of {internal.length + external.length} areas assessed</div>
            <div className="as-progress-track"><div className="as-progress-fill" style={{ width: `${(internal.length + external.length) > 0 ? (assessedCount / (internal.length + external.length)) * 100 : 0}%` }} /></div>
          </div>
          {unmeasuredCount > 0 && (
            <div className="as-unmeasured-notice">
              <Icon name="clock" size={14} style={{marginRight:4}} /> {unmeasuredCount} area{unmeasuredCount > 1 ? 's' : ''} not yet measured — {unmeasuredCount > 1 ? 'they' : 'it'} will appear here once the measurer has added surfaces
            </div>
          )}
          {(internal.length + external.length) === 0 && (
            <div className="as-empty-state" style={{marginTop:24,textAlign:'center',color:'var(--text-2,#6b7280)'}}>
              <div style={{fontSize:32,marginBottom:8}}><Icon name="ruler" size={32} /></div>
              <div>No measured areas yet</div>
              <div style={{fontSize:13,marginTop:4}}>The measurer needs to add surfaces before you can assess</div>
            </div>
          )}
          {allAreasComplete ? (
            <>
              {/* ── Assessment complete banner ── */}
              <div className="as-complete-banner">
                <div className="as-complete-icon">✅</div>
                <div className="as-complete-title">Assessment complete</div>
                <div className="as-complete-summary">
                  {(() => {
                    let totalAssessed = 0, totalMeasured = 0;
                    measurableAreas.forEach(a => {
                      const ad = localData[a.firestoreId];
                      if (ad) {
                        Object.values(ad.assessBySubstrate || {}).forEach(v => { totalAssessed += parseFloat(v.hours) || 0; });
                        totalAssessed += parseFloat(ad.washdownHours) || 0;
                      }
                      totalMeasured += calcAreaHours(a).total;
                    });
                    const diff = totalAssessed - totalMeasured;
                    const pct = totalMeasured > 0 ? Math.round((diff / totalMeasured) * 100) : 0;
                    return (
                      <>
                        <div className="as-complete-stat">
                          <span>📏 Measured (calc)</span>
                          <span>{totalMeasured.toFixed(1)}h</span>
                        </div>
                        <div className="as-complete-stat">
                          <span>📋 Your assessed</span>
                          <span>{totalAssessed.toFixed(1)}h</span>
                        </div>
                        {totalMeasured > 0 && (
                          <div className={`as-complete-stat as-complete-diff ${Math.abs(pct) > 30 ? 'as-diff-warn' : Math.abs(pct) > 15 ? 'as-diff-caution' : 'as-diff-ok'}`}>
                            <span>Δ Difference</span>
                            <span>{pct > 0 ? '+' : ''}{pct}%</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div className="as-complete-next">
                  <div className="as-complete-next-label">Next step</div>
                  <div className="as-complete-next-text">Notify the BDM/Estimator so they can review your assessed hours and approve.</div>
                </div>
                <button className="as-complete-notify-btn" onClick={handleComplete}>
                  ✉️ Notify BDM Now →
                </button>
                <button className="as-complete-review-btn" onClick={() => {
                  const first = measurableAreas[0];
                  if (first) { setAreaIdx(areas.indexOf(first)); window.scrollTo(0, 0); }
                }}>
                  ← Review an area before completing
                </button>
              </div>
              {/* Area cards — collapsed below for reference */}
              <div className="as-section-label" style={{marginTop:16,fontSize:12,color:'#94a3b8'}}>Areas assessed</div>
              {internal.length > 0 && <div className="as-pick-group-label">🏠 Internal ({internal.length})</div>}
              {internal.map(renderPickerCard)}
              {external.length > 0 && <div className="as-pick-group-label" style={{marginTop:12}}>🏗️ External ({external.length})</div>}
              {external.map(renderPickerCard)}
            </>
          ) : (
            <>
              <div className="as-section-label" style={{marginTop:12}}>Select an area to assess</div>
              {internal.length > 0 && <div className="as-pick-group-label">🏠 Internal ({internal.length})</div>}
              {internal.map(renderPickerCard)}
              {external.length > 0 && <div className="as-pick-group-label" style={{marginTop:12}}>🏗️ External ({external.length})</div>}
              {external.map(renderPickerCard)}
            </>
          )}

          {/* Site-wide capture — project-level, not tied to any one area */}
          <div className="as-section-label" style={{ marginTop: 20 }}>Site-wide</div>
          <div className="as-pick-card as-pick-site" onClick={() => setSubScreen('siteItems')}>
            <span className="as-pick-icon">🧰</span>
            <div className="as-pick-info">
              <div className="as-pick-name">Site Items</div>
              <div className="as-pick-meta">{recordedSiteItems} of {SITE_ITEMS.length} recorded</div>
            </div>
            <span className={`as-pick-status as-pick-st-${siteItemsStatus}`}>
              {siteItemsStatus === 'done' ? '✓ Done' : siteItemsStatus === 'partial' ? '◐ Partial' : '○ Not started'}
            </span>
          </div>
          <div className="as-pick-card as-pick-site" onClick={() => setSubScreen('flooring')}>
            <span className="as-pick-icon">🪵</span>
            <div className="as-pick-info">
              <div className="as-pick-name">Internal Flooring</div>
              <div className="as-pick-meta">{flooringCount} floor covering{flooringCount === 1 ? '' : 's'} recorded</div>
            </div>
          </div>

          {/* Add Missing Area — always available in picker */}
          <button className="as-add-area-btn" onClick={() => setShowAddArea(true)}>
            ＋ Add Missing Area
          </button>

          {/* Add Missing Area modal */}
          {showAddArea && <AddMissingAreaModal
            onAdd={async (data) => {
              await onCreatePMArea(data);
              setShowAddArea(false);
            }}
            onClose={() => setShowAddArea(false)}
          />}
        </div>
      </div>
    );
  }

  const areaId = area.firestoreId;
  const d = localData[areaId] || { assessBySubstrate: {}, assessNotes: '', accessNeeds: [], accessNeedsOtherText: '', pmAddedSurfaces: [], washdownHours: '' };
  const substrateSummary = getSubstrateSummary(area);

  // All substrates: from measurements + PM-added surfaces + PM-created area surfaces
  const measuredSubstrates = Object.keys(substrateSummary);
  const pmAdded = d.pmAddedSurfaces || [];
  const pmAreaSurfaces = area.pmSurfaces || [];
  const allSubstrates = [...new Set([...measuredSubstrates, ...pmAdded, ...pmAreaSurfaces])];

  const surfaceList = area.type === 'internal'
    ? ['Floor', 'Ceilings', 'Ceilings Detailed', 'Walls', 'Walls Detailed', 'Windows', 'Doors', 'Skirting', 'Stairs']
    : ['Gutter', 'Fascia', 'Eaves', 'Soffit', 'Walls - Flat', 'Walls Rough', 'Windows', 'Downpipes', 'Doors', 'Balustrades/Handrails', 'Posts', 'Roofs'];

  // ── Update helpers ──────────────────────────────────────────────────────
  const updateField = (field, value) => {
    dirty.current = true;
    setLocalData(prev => ({
      ...prev,
      [areaId]: { ...prev[areaId], [field]: value },
    }));
  };

  const updateSubstrate = (substrate, key, value, _skipAdvance = false) => {
    dirty.current = true;
    setLocalData(prev => {
      const updated = {
        ...prev,
        [areaId]: {
          ...prev[areaId],
          assessBySubstrate: {
            ...(prev[areaId]?.assessBySubstrate || {}),
            [substrate]: {
              ...(prev[areaId]?.assessBySubstrate?.[substrate] || {}),
              [key]: value,
            },
          },
        },
      };
      // Check hoursDone using the just-computed updated state (synchronous, no nested setState)
      if (key === 'hours') {
        const newHours = updated[areaId].assessBySubstrate;
        const washdownOk = area.type !== 'external' || parseFloat(updated[areaId]?.washdownHours) > 0 || parseFloat(value) > 0;
        const allFilled = allSubstrates.length > 0 && allSubstrates.every(s => parseFloat(newHours?.[s]?.hours) > 0) && washdownOk;
        if (allFilled) {
          // Schedule hoursDone update after this render cycle
          Promise.resolve().then(() => setHoursDone(h => ({ ...h, [areaId]: true })));
        }
      }
      return updated;
    });
  };

  const updateCondition = (substrate, key, value, userEdited = false) => {
    dirty.current = true;
    setLocalData(prev => {
      const prevCond = prev[areaId]?.conditionBySubstrate || {};
      const prevEntry = prevCond[substrate] || {};
      const nextEntry = { ...prevEntry, [key]: value };
      if (key === 'note' && userEdited) nextEntry.noteEdited = true;
      if (key === 'note' && !userEdited) nextEntry.noteEdited = false;
      let nextCond;
      if (key === 'condition' && value === null && !nextEntry.note && !(nextEntry.defects && nextEntry.defects.length)) {
        const { [substrate]: _drop, ...rest } = prevCond;
        nextCond = rest;
      } else {
        nextCond = { ...prevCond, [substrate]: nextEntry };
      }
      return {
        ...prev,
        [areaId]: { ...prev[areaId], conditionBySubstrate: nextCond },
      };
    });
  };

  const addMissedSurface = (name) => {
    dirty.current = true;
    setLocalData(prev => ({
      ...prev,
      [areaId]: {
        ...prev[areaId],
        pmAddedSurfaces: [...(prev[areaId]?.pmAddedSurfaces || []), name],
      },
    }));
  };

  const goTo = async (newIdx) => {
    try { await saveCurrentArea(areaIdx); } catch {}
    setAreaIdx(newIdx);
    setActiveSubstrate(null);
    setHoursDone(h => ({ ...h })); // reset any stale state
    window.scrollTo(0, 0);
  };

  const goBackToAreas = async () => {
    try { await saveCurrentArea(areaIdx); } catch {}
    setAreaIdx(-1);
    setActiveSubstrate(null);
    window.scrollTo(0, 0);
  };

  // ── Area-level calculated hours ──────────────────────────────────────────
  const measCalc = calcAreaHours(area);
  const totalAssessHours = Object.values(d.assessBySubstrate).reduce((s, v) => s + (parseFloat(v.hours) || 0), 0);
  const washdownHoursNum = parseFloat(d.washdownHours) || 0;
  const totalAssessWithWashdown = totalAssessHours + washdownHoursNum;

  return (
    <div className="as-screen">
      {/* Header */}
      <div className="s2s-header">
        <button className="s2s-back-btn" onClick={handleBack}>← Back</button>
        <div className="s2s-header-center">
          <div className="s2s-header-title">📋 Assess Mode</div>
          <div className="s2s-header-sub">{project.projectName || project.clientName || 'Project'}</div>
        </div>
        <div className="as-save-indicator">{saving ? <span className="spinner spinner-sm"></span> : dirty.current ? '●' : <Icon name="check" size={14} />}</div>
      </div>

      {/* Welcome banner */}
      {showWelcome && (
        <div className="s2s-mode-banner as-banner">
          <div className="s2s-banner-text">
            <strong>You're in Assessment Mode</strong>
            <span>Review each area's photos and scope, then enter your estimated paint hours per surface. Your work is saved automatically.</span>
          </div>
          <button className="s2s-banner-dismiss" aria-label="Dismiss" onClick={() => setShowWelcome(false)}><Icon name="close" size={14} /></button>
        </div>
      )}

      {/* Progress bar */}
      <div className="as-progress">
        <div className="as-progress-text">{assessedCount} of {areas.length} areas assessed</div>
        <div className="as-progress-track">
          <div className="as-progress-fill" style={{ width: `${areas.length > 0 ? (assessedCount / areas.length) * 100 : 0}%` }} />
        </div>
        <div className="as-area-pills">
          {areas.map((a, i) => {
            const ad = localData[a.firestoreId];
            const measuredSubs = [...new Set((a.surfaceEntries || []).map(e => e.substrate).filter(Boolean))];
            const fullyAssessed = ad && measuredSubs.length > 0 && measuredSubs.every(sub => parseFloat(ad.assessBySubstrate?.[sub]?.hours) > 0);
            const hasAny = ad && Object.values(ad.assessBySubstrate || {}).some(v => parseFloat(v.hours) > 0);
            return (
              <button key={a.firestoreId || i}
                className={`as-area-pill ${i === areaIdx ? 'current' : ''} ${fullyAssessed ? 'done' : hasAny ? 'partial' : ''}`}
                onClick={() => goTo(i)}>
                {i + 1}
              </button>
            );
          })}
        </div>
      </div>

      {/* Area content */}
      <div className="as-content">
        {/* Desktop: two-column layout. Mobile: single column */}
        <div className="as-layout">

          {/* ── Left: context panel (photos, notes, totals) ── */}
          <div className="as-context-col">
            {/* Area header */}
            <div className="as-area-header">
              <span className="as-area-icon">{area.type === 'internal' ? '🏠' : '🏗️'}</span>
              <div>
                <div className="as-area-name">{area.name || 'Unnamed Area'}{area.pmAdded && <span className="pd-area-pm-badge" style={{marginLeft:8}}>PM Added</span>}</div>
                <div className="as-area-type">{area.type === 'internal' ? 'Internal' : 'External'}</div>
              </div>
            </div>

            {/* Photos — hero element */}
            <PhotoCarousel photos={d.photos || area.photos || []} />

            {/* PM photo capture */}
            <div className="as-pm-photo-section">
              <button className="as-pm-photo-btn" onClick={() => pmCameraRef.current?.click()}>📷 Add PM Photo</button>
              <button className="as-pm-photo-btn as-pm-photo-lib" onClick={() => pmUploadRef.current?.click()}>📁 Library</button>
              <input type="file" accept="image/*" capture="environment" ref={pmCameraRef} onChange={e => { handlePMPhoto(e.target.files[0]); e.target.value = ''; }} style={{display:'none'}} />
              <input type="file" accept="image/*" ref={pmUploadRef} onChange={e => { handlePMPhoto(e.target.files[0]); e.target.value = ''; }} style={{display:'none'}} />
            </div>

            {/* BDM scope notes (read-only) */}
            {area.scopeNotes && (
              <div className="as-bdm-notes">
                <div className="as-section-label">💬 BDM Scope Notes</div>
                <div className="as-bdm-notes-text">{area.scopeNotes}</div>
              </div>
            )}
            {area.scopeExclusions && (
              <div className="as-bdm-excl">
                <div className="as-section-label">🚫 Exclusions</div>
                <div className="as-bdm-excl-text">{area.scopeExclusions}</div>
              </div>
            )}
            {area.defectsNote && (
              <div className="as-bdm-defects">
                <div className="as-section-label">⚠ Defects / Remediation</div>
                <div className="as-bdm-defects-text">{area.defectsNote}</div>
              </div>
            )}
            {area.pmAdded && area.pmNote && (
              <div className="as-bdm-notes" style={{ background: '#fffbeb', borderColor: '#fbbf24' }}>
                <div className="as-section-label">📝 PM Note (when area was added)</div>
                <div className="as-bdm-notes-text">{area.pmNote}</div>
              </div>
            )}
            {area.pmAdded && (
              <div className="as-bdm-notes" style={{ background: '#f0fdf4', borderColor: '#86efac' }}>
                <div className="as-section-label">ℹ️ PM-Added Area</div>
                <div className="as-bdm-notes-text" style={{ fontSize: 12 }}>
                  Added by {area.pmAddedBy || 'PM'}. Surfaces flagged for measurement: {(area.pmSurfaces || []).join(', ') || 'none specified'}.
                  {'\n'}The measurer will be notified to add dimensions.
                </div>
              </div>
            )}

            {/* Hours comparison */}
            <div className="as-totals-card">
              <div className="as-totals-row">
                <span>📏 Measured hours (calc)</span>
                <span className="as-totals-val">{measCalc.total.toFixed(1)}h</span>
              </div>
              {area.type === 'external' && washdownHoursNum > 0 && (
                <div className="as-totals-row">
                  <span>🚿 Washdown hours</span>
                  <span className="as-totals-val">{washdownHoursNum.toFixed(1)}h</span>
                </div>
              )}
              <div className="as-totals-row as-totals-assess">
                <span>📋 Your assessed hours</span>
                <span className="as-totals-val">
                  {totalAssessWithWashdown > 0
                    ? <>{totalAssessWithWashdown.toFixed(1)}h{washdownHoursNum > 0 && <small>{washdownHoursNum.toFixed(1)}h washdown</small>}</>
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* ── Right: work panel (hours entry, checklist, tags, notes) ── */}
          <div className="as-work-col">

        {/* Surface list — accordion guided step-through */}
        {allSubstrates.length === 0 ? (
          <div className="as-start-here">
            <div className="as-start-icon">📋</div>
            <div className="as-start-text">{area.pmAdded ? 'Add surfaces using "Flag a missed surface" below' : 'No surfaces measured for this area'}</div>
          </div>
        ) : (
          <>
            {!activeSubstrate && !hoursDone[areaId] && (
              <button className="af-start-measuring-btn" style={{margin:'8px 0 12px'}}
                onClick={() => setActiveSubstrate(area.type === 'external' ? 'washdown' : allSubstrates[0])}>
                📋 Start entering hours →
              </button>
            )}
            <div className="as-surfaces">
              {/* Washdown — external areas only, always first */}
              {area.type === 'external' && (() => {
                const isActive = activeSubstrate === 'washdown';
                const hasHours = parseFloat(d.washdownHours) > 0;
                const firstSurface = allSubstrates[0];
                return (
                  <div key="washdown" ref={el => { surfaceRowRefs.current['washdown'] = el; }}
                    className={`as-surface-wrap ${isActive ? 'as-surface-active' : ''}`}>
                    {!isActive && (
                      <div className="as-surface-collapsed" onClick={() => setActiveSubstrate('washdown')}>
                        <span className={`as-col-status ${hasHours ? 'done' : ''}`}>{hasHours ? '✓' : '○'}</span>
                        <span className="as-col-name">🚿 Washdown Hours</span>
                        {hasHours && <span className="as-col-hours">{d.washdownHours}h</span>}
                      </div>
                    )}
                    {isActive && (
                      <>
                        <div className="as-surface-row">
                          <div className="as-surface-header">
                            <div className="as-surface-name">🚿 Washdown Hours</div>
                            <div className="as-surface-measured" style={{fontSize:11,color:'#64748b'}}>External area prep</div>
                          </div>
                          <div className="as-washdown-hint">Hours to pressure wash / prepare this external area before painting</div>
                          <div className="as-hours-row">
                            <label className="as-hours-label">Washdown hours</label>
                            <input className="as-hours-input" type="text" inputMode="decimal"
                              placeholder="—" value={d.washdownHours}
                              autoFocus
                              onChange={e => updateField('washdownHours', e.target.value)} />
                          </div>
                        </div>
                        <button className="af-next-surface-btn"
                          onClick={() => setActiveSubstrate(firstSurface || null)}>
                          {firstSurface ? `Next: ${firstSurface} →` : 'Done — review notes'}
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}
              {allSubstrates.map((sub, idx) => {
                const isActive = activeSubstrate === sub;
                const hasHours = parseFloat(d.assessBySubstrate?.[sub]?.hours) > 0;
                const isLast = idx === allSubstrates.length - 1;
                const nextSub = allSubstrates[idx + 1];
                return (
                  <div key={sub} ref={el => { surfaceRowRefs.current[sub] = el; }}
                    className={`as-surface-wrap ${isActive ? 'as-surface-active' : ''}`}>
                    {/* Collapsed header */}
                    {!isActive && (
                      <div className="as-surface-collapsed" onClick={() => setActiveSubstrate(sub)}>
                        <span className={`as-col-status ${hasHours ? 'done' : ''}`}>{hasHours ? '✓' : '○'}</span>
                        <span className="as-col-name">{sub}{pmAdded.includes(sub) ? ' ⚠' : ''}</span>
                        {hasHours && <span className="as-col-hours">{d.assessBySubstrate[sub].hours}h</span>}
                      </div>
                    )}
                    {/* Expanded row */}
                    {isActive && (
                      <>
                        <SurfaceAssessRow substrate={sub}
                          summary={substrateSummary[sub] || null}
                          assessData={d.assessBySubstrate[sub]}
                          conditionData={d.conditionBySubstrate?.[sub]}
                          addedByPM={pmAdded.includes(sub) || pmAreaSurfaces.includes(sub)}
                          onChangeHours={(v, skip) => updateSubstrate(sub, 'hours', v, skip)}
                          onChangeNote={v => updateSubstrate(sub, 'note', v)}
                          onChangeCondition={(key, value, userEdited) => updateCondition(sub, key, value, userEdited)} />
                        <button
                          className={`af-next-surface-btn ${isLast ? 'af-next-surface-btn-last' : ''}`}
                          onClick={() => {
                            if (isLast) { setActiveSubstrate(null); }
                            else setActiveSubstrate(nextSub);
                          }}>
                          {isLast ? 'All surfaces done' : `Next: ${nextSub} →`}
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Assessor checklist — shows when surfaces are done and no surface is active */}
            {!activeSubstrate && allSubstrates.some(s => parseFloat(d.assessBySubstrate?.[s]?.hours) > 0) && (
              <div className="af-checklist as-checklist">
                <div className="af-checklist-title">Hours entered — review before saving</div>
                <div className="af-checklist-items">
                  <label className="af-checklist-item">
                    <input type="checkbox" checked={!!(area.scopeNotes||'').trim()} readOnly />
                    <span style={{opacity: (area.scopeNotes||'').trim() ? 1 : 0.5}}>💬 Measurer scope notes {(area.scopeNotes||'').trim() ? '— reviewed' : '— none entered'}</span>
                  </label>
                  <label className="af-checklist-item">
                    <input type="checkbox" checked={!!(area.scopeExclusions||'').trim()} readOnly />
                    <span style={{opacity: (area.scopeExclusions||'').trim() ? 1 : 0.5}}>🚫 Exclusions {(area.scopeExclusions||'').trim() ? '— reviewed' : '— none entered'}</span>
                  </label>
                  <label className="af-checklist-item">
                    <input type="checkbox" checked={!!(area.defectsNote||'').trim()} readOnly />
                    <span style={{opacity: (area.defectsNote||'').trim() ? 1 : 0.5}}>⚠ Defects / remediation {(area.defectsNote||'').trim() ? '— reviewed' : '— none entered'}</span>
                  </label>
                  <label className="af-checklist-item" onClick={() => {
                    if (accessRef.current) {
                      const top = accessRef.current.getBoundingClientRect().top + window.scrollY - 72;
                      window.scrollTo({ top, behavior: 'smooth' });
                    }
                  }}>
                    <input type="checkbox" checked={(d.accessNeeds||[]).length > 0} readOnly />
                    <span>🦺 Access &amp; safety tagged</span>
                  </label>
                  <label className="af-checklist-item" onClick={() => {
                    if (notesRef.current) {
                      const top = notesRef.current.getBoundingClientRect().top + window.scrollY - 72;
                      window.scrollTo({ top, behavior: 'smooth' });
                    }
                  }}>
                    <input type="checkbox" checked={!!(d.assessNotes||'').trim()} readOnly />
                    <span>📝 PM assessment notes added</span>
                  </label>
                </div>
              </div>
            )}
          </>
        )}

        {/* Add missed surface or area */}
        <AddMissedSurface surfaceList={surfaceList.filter(s => !allSubstrates.includes(s))} onAdd={addMissedSurface} />
        <button className="as-add-area-btn" style={{ marginTop: 8 }} onClick={() => setShowAddArea(true)}>
          ＋ Add Missing Area
        </button>
        {showAddArea && <AddMissingAreaModal
          onAdd={async (data) => { await onCreatePMArea(data); setShowAddArea(false); }}
          onClose={() => setShowAddArea(false)}
        />}

        {/* Access / safety needs */}
        <div className="as-section" ref={accessRef}>
          <div className="as-section-label">🦺 Access & Safety Needs</div>
          <AccessTags selected={d.accessNeeds} onChange={v => updateField('accessNeeds', v)} areaType={area.type}
            otherLabels={d.accessOtherLabels||area.accessOtherLabels||{}} onOtherLabelChange={(id, val) => updateField('accessOtherLabels', {...(d.accessOtherLabels||area.accessOtherLabels||{}), [id]: val})} />
        </div>

        {/* PM notes */}
        <div className="as-section" ref={notesRef}>
          <div className="as-section-label">📝 PM Assessment Notes</div>
          <textarea className="as-pm-notes" rows={4}
            placeholder="Overall observations, access concerns, condition notes..."
            value={d.assessNotes} onChange={e => updateField('assessNotes', e.target.value)} />
        </div>

          </div>{/* /as-work-col */}
        </div>{/* /as-layout */}
      </div>

      {/* Navigation */}
      <div className="as-nav-bar">
        <button className="as-nav-prev" disabled={saving} onClick={goBackToAreas}>
          ← Areas
        </button>
        <div className="as-nav-center">
          {areaIdx + 1} / {areas.length}
        </div>
        {areaIdx < areas.length - 1 ? (
          <button className="as-nav-next" disabled={saving} onClick={() => goTo(areaIdx + 1)}>
            {saving ? <span className="spinner spinner-sm spinner-white"></span> : <>Next <Icon name="chevron-right" size={14} /></>}
          </button>
        ) : (
          <button className="as-nav-next" disabled={saving} onClick={goBackToAreas}>
            {saving ? <span className="spinner spinner-sm spinner-white"></span> : <>Save & Back <Icon name="chevron-right" size={14} /></>}
          </button>
        )}
      </div>
    </div>
  );
}
